import React, { useEffect, useState } from "react";

/**
 * CountdownTimer
 *  - If `startTime` is provided and is still in the future, the timer counts down to
 *    the AUCTION START and (when `showLabel`) shows a "Starts in" prefix.
 *  - Otherwise it counts down to `endTime` (lot/auction close) and shows "Ends in".
 *  - Once `endTime` passes, the optional `onEnd` callback fires.
 *  - `showLabel` (default true for compact) toggles the inline label so callers that
 *    already render their own label (e.g. LotDetail) can suppress it.
 *  - `preStart` (boolean, read-only) is reflected back to the caller via `onPreStartChange`.
 */
export default function CountdownTimer({
  endTime,
  startTime,
  onEnd,
  compact = false,
  showLabel = true,
  dataTestId,
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const end = endTime ? new Date(endTime).getTime() : 0;
  const start = startTime ? new Date(startTime).getTime() : 0;

  // Before start → count to start. Otherwise → count to end.
  const preStart = !!start && now < start;
  const target = preStart ? start : end;
  const diff = Math.max(0, target - now);

  useEffect(() => {
    if (!preStart && diff === 0 && onEnd) onEnd();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diff === 0, preStart]);

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const mins = Math.floor((diff / (1000 * 60)) % 60);
  const secs = Math.floor((diff / 1000) % 60);
  const ending = !preStart && diff < 60_000 && diff > 0;

  const label = preStart ? "Starts in" : "Ends in";

  if (compact) {
    return (
      <span
        data-testid={dataTestId}
        className={`font-mono text-sm inline-flex items-baseline gap-1 ${ending ? "text-[var(--ia-red)] font-semibold" : ""}`}
      >
        {showLabel && (
          <span className="tracking-eyebrow text-[10px] text-[var(--ia-muted)] uppercase">{label}</span>
        )}
        <span>
          {days > 0 ? `${days}d ` : ""}
          {String(hours).padStart(2, "0")}:{String(mins).padStart(2, "0")}:{String(secs).padStart(2, "0")}
        </span>
      </span>
    );
  }

  return (
    <div data-testid={dataTestId} className="flex items-center gap-3">
      {["D", "H", "M", "S"].map((l, i) => {
        const v = [days, hours, mins, secs][i];
        return (
          <div key={l} className={`px-3 py-2 border ${ending ? "border-[var(--ia-red)]" : "border-black"} min-w-[58px] text-center`}>
            <div className={`font-mono text-2xl font-bold leading-none ${ending ? "text-[var(--ia-red)]" : ""}`}>{String(v).padStart(2, "0")}</div>
            <div className="tracking-eyebrow text-[10px] mt-1 text-[var(--ia-muted)]">{l}</div>
          </div>
        );
      })}
    </div>
  );
}
